import { CheckCircle2Icon, CloudUploadIcon, FileIcon } from 'lucide-react';
import { type DragEvent, useRef, useState } from 'react';
import type { Control, FieldPath, FieldValues } from 'react-hook-form';

import { Button } from '@/components/core/button';
import {
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormMessage,
} from '@/components/core/form';
import { Input } from '@/components/core/input';
import { cn } from '@/shared/helpers';

import { computeSha256Hex, REQUIRED_CSV_COLUMNS, validateCsvHeader } from './inputs-upload';

interface CsvFileFieldProps<TFieldValues extends FieldValues> {
  control: Control<TFieldValues>;
  name: FieldPath<TFieldValues>;
}

type ValidationState =
  | { kind: 'idle' }
  | { kind: 'validating' }
  | { kind: 'ok'; hash: string; size: number }
  | { kind: 'invalid'; missing: string[] }
  | { kind: 'wrong-type'; fileName: string }
  | { kind: 'error'; message: string };

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
}

function isCsvFile(file: File): boolean {
  const lowerName = file.name.toLowerCase();
  // application/vnd.ms-excel covers both .csv and .xls; rule out spreadsheets explicitly.
  if (lowerName.endsWith('.xls') || lowerName.endsWith('.xlsx')) return false;
  if (lowerName.endsWith('.csv')) return true;
  return file.type === 'text/csv' || file.type === 'application/vnd.ms-excel';
}

export function CsvFileField<TFieldValues extends FieldValues>({
  control,
  name,
}: CsvFileFieldProps<TFieldValues>) {
  const inputRef = useRef<HTMLInputElement | null>(null);
  // Tracks the most recent acceptFile invocation so concurrent drops can be
  // discarded — otherwise an in-flight hash from drop A racing drop B can
  // leave the form's File and the displayed validation state belonging to
  // different files.
  const requestIdRef = useRef(0);
  const [validationState, setValidationState] = useState<ValidationState>({ kind: 'idle' });
  const [isDragging, setIsDragging] = useState(false);

  return (
    <FormField
      control={control}
      name={name}
      render={({ field }) => {
        const fileValue = field.value as unknown as File | undefined;
        const hasFile = fileValue instanceof File;
        const openPicker = () => inputRef.current?.click();

        const acceptFile = async (file: File) => {
          if (!isCsvFile(file)) {
            setValidationState({ kind: 'wrong-type', fileName: file.name });
            field.onChange(undefined);
            if (inputRef.current) inputRef.current.value = '';
            return;
          }
          requestIdRef.current += 1;
          const requestId = requestIdRef.current;
          setValidationState({ kind: 'validating' });
          try {
            const headerCheck = await validateCsvHeader(file);
            if (requestId !== requestIdRef.current) return;
            if (!headerCheck.ok) {
              setValidationState({ kind: 'invalid', missing: headerCheck.missing });
              field.onChange(undefined);
              return;
            }
            const hash = await computeSha256Hex(file);
            if (requestId !== requestIdRef.current) return;
            field.onChange(file);
            setValidationState({ kind: 'ok', hash, size: file.size });
          } catch (error) {
            if (requestId !== requestIdRef.current) return;
            setValidationState({
              kind: 'error',
              message: error instanceof Error ? error.message : 'Failed to read file',
            });
            field.onChange(undefined);
          } finally {
            if (inputRef.current) {
              inputRef.current.value = '';
            }
          }
        };

        const onDragOver = (event: DragEvent) => {
          event.preventDefault();
          if (!isDragging) setIsDragging(true);
        };
        const onDragLeave = (event: DragEvent) => {
          // dragenter/leave bubble through children; ignore the leave when the
          // cursor crosses into a descendant or the border strobes as the user
          // holds a file over the dropzone.
          if (event.currentTarget.contains(event.relatedTarget as Node | null)) return;
          setIsDragging(false);
        };
        const onDrop = (event: DragEvent) => {
          event.preventDefault();
          setIsDragging(false);
          const file = event.dataTransfer.files[0];
          if (file) void acceptFile(file);
        };
        const dropzoneClassName = cn(
          'flex flex-col items-center justify-center gap-2 rounded-md border border-dashed border-muted-foreground/40 bg-muted/30 px-4 py-10 text-muted-foreground transition-colors',
          isDragging && 'border-primary bg-primary/5 text-foreground',
        );

        return (
          <FormItem>
            <FormControl>
              <div className="grid gap-3">
                <Input
                  ref={inputRef}
                  type="file"
                  accept=".csv,text/csv"
                  className="hidden"
                  onChange={(event) => {
                    const file = event.target.files?.[0];
                    if (file) void acceptFile(file);
                  }}
                />
                {hasFile ? (
                  <div
                    className={dropzoneClassName}
                    onDragOver={onDragOver}
                    onDragLeave={onDragLeave}
                    onDrop={onDrop}
                  >
                    <div className="flex w-full items-center gap-2 text-foreground">
                      <FileIcon />
                      <div className="flex-1 truncate">
                        <div className="text-sm font-medium">{fileValue.name}</div>
                        <div className="text-xs text-muted-foreground">
                          {formatBytes(fileValue.size)}
                          {validationState.kind === 'ok' ? (
                            <>
                              {' · '}
                              <code className="font-mono text-[11px]">
                                sha256: {validationState.hash.slice(0, 12)}...
                              </code>
                            </>
                          ) : null}
                        </div>
                      </div>
                      {validationState.kind === 'ok' ? (
                        <CheckCircle2Icon className="text-state-success-foreground" />
                      ) : null}
                    </div>
                    <Button type="button" size="sm" variant="secondary" onClick={openPicker}>
                      Replace file
                    </Button>
                  </div>
                ) : (
                  <button
                    type="button"
                    onClick={openPicker}
                    onDragOver={onDragOver}
                    onDragLeave={onDragLeave}
                    onDrop={onDrop}
                    className={cn(
                      dropzoneClassName,
                      'cursor-pointer hover:bg-muted/50 focus-visible:ring-2 focus-visible:ring-ring focus-visible:outline-none',
                    )}
                  >
                    <CloudUploadIcon className="size-8" />
                    <div className="text-sm font-medium">
                      Click to upload or drag and drop CSV file here
                    </div>
                  </button>
                )}
                {validationState.kind === 'invalid' ? (
                  <p className="text-sm text-destructive">
                    Missing required column{validationState.missing.length > 1 ? 's' : ''}:{' '}
                    <code className="font-mono">{validationState.missing.join(', ')}</code>
                  </p>
                ) : null}
                {validationState.kind === 'wrong-type' ? (
                  <p className="text-sm text-destructive">
                    Only CSV files are supported. You picked{' '}
                    <code className="font-mono">{validationState.fileName}</code>.
                  </p>
                ) : null}
                {validationState.kind === 'error' ? (
                  <p className="text-sm text-destructive">{validationState.message}</p>
                ) : null}
              </div>
            </FormControl>
            <FormDescription>
              Required columns: <code className="font-mono">{REQUIRED_CSV_COLUMNS.join(', ')}</code>
              . File is hashed in the browser and uploaded directly to S3 via a presigned URL.
            </FormDescription>
            <FormMessage />
          </FormItem>
        );
      }}
    />
  );
}
